import type { Bytes } from "@pdfking/tfunc"
import type { Allocated } from "../types/Allocated"
import type { Pointer } from "../types/Pointer"
import type { WasmExportConfig } from "../types/WasmExportConfig"
import type { WasmInstance } from "../types/WasmInstance"
import { UrlLoad } from "@pdfking/compatibility"
import { Ceil, Max, Min } from "@pdfking/math"
import {
  Constant,
  IsFunction,
  Items,
  Keys,
  RecordAssign,
  StringToUInt8,
  Void,
} from "@pdfking/tfunc"
import { Stub } from "../types/WasmExportConfig"

// ── ConvertJsFunctionToWasm helpers ─────────────────────────────────

const WASM_TYPE_NAMES: Readonly<Record<string, string>> = {
  d: "f64",
  f: "f32",
  i: "i32",
  j: "i64",
  p: "i32",
}

const WASM_TYPE_CODES: Readonly<Record<string, number>> = {
  d: 124,
  f: 125,
  i: 127,
  j: 126,
  p: 127,
}

const SigToWasmTypes = (sig: string) => ({
  parameters: [...sig.slice(1)].map(c => WASM_TYPE_NAMES[c]),
  results: sig[0] === "v" ? [] : [WASM_TYPE_NAMES[sig[0]]],
})

const Uleb128Encode = (n: number, target: number[]) => {
  if (n < 128) target.push(n)
  else target.push((n % 128) | 128, n >> 7)
}

const GenerateFuncType = (sig: string, target: number[]) => {
  const sigParam = sig.slice(1)
  target.push(96)
  Uleb128Encode(sigParam.length, target)
  for (const c of sigParam) target.push(WASM_TYPE_CODES[c])
  if (sig[0] === "v") target.push(0)
  else target.push(1, WASM_TYPE_CODES[sig[0]])
}

const ConvertJsFunctionToWasm = (
  func: (...args: number[]) => number,
  sig: string
): WebAssembly.ExportValue => {
  const WasmFunction = (WebAssembly as unknown as Record<string, unknown>)
    .Function as new (
    type: unknown,
    func: unknown
  ) => WebAssembly.ExportValue
  if (IsFunction(WasmFunction)) {
    return new WasmFunction(SigToWasmTypes(sig), func)
  }
  const typeSectionBody: number[] = [1]
  GenerateFuncType(sig, typeSectionBody)
  const bytes: number[] = [0, 97, 115, 109, 1, 0, 0, 0, 1]
  Uleb128Encode(typeSectionBody.length, bytes)
  bytes.push(...typeSectionBody)
  bytes.push(2, 7, 1, 1, 101, 1, 102, 0, 0, 7, 5, 1, 1, 102, 0, 0)
  const module = new WebAssembly.Module(new Uint8Array(bytes))
  const instance = new WebAssembly.Instance(module, { e: { f: func } })
  return instance.exports.f
}

// ── CreateEmscriptenImports ─────────────────────────────────────────

const Fail = (msg: string): never => {
  throw new Error(msg)
}

const DATE = Constant(1704067200000)
const MAX_HEAP = 2147483648
const Align = (size: number, alignment: number) =>
  Ceil(size / alignment) * alignment

const CreateInvoke =
  (
    exports: Record<string, unknown>,
    config: WasmExportConfig,
    returnsValue: boolean
  ) =>
  (index: number, ...args: number[]) => {
    const stackGet = exports[config.stackGetCurrent] as () => number
    const sp = stackGet()
    try {
      const table = exports[config.indirectFunctionTable] as WebAssembly.Table
      const fn = table.get(index) as ((...a: number[]) => number) | null
      if (fn) return fn(...args)
      return 0
    } catch (e: unknown) {
      const stackRestore = exports[config.stackRestore] as (sp: number) => void
      stackRestore(sp)
      if (typeof e !== "number") throw e
      const setThrew = exports[config.setThrew] as (
        a: number,
        b: number
      ) => void
      setThrew(1, 0)
      return returnsValue ? 0 : undefined
    }
  }

const CreateEmscriptenImports = (
  config: WasmExportConfig
): {
  readonly exports: Record<string, unknown>
  readonly imports: WebAssembly.Imports
} => {
  const exports = {} as Record<string, unknown>
  const moduleImports: WebAssembly.ModuleImports = {}

  for (const [name, returnsValue] of Items(config.invokes)) {
    moduleImports[name] = CreateInvoke(exports, config, returnsValue)
  }

  const mem = () => exports[config.memory] as WebAssembly.Memory

  const stubs: Record<Stub, WebAssembly.ModuleImports[string]> = {
    [Stub.noop]: Void,
    [Stub.zero]: Constant(0),
    [Stub.enoent]: Constant(-44),
    [Stub.ebadf]: Constant(8),
    [Stub.ebadf_neg]: Constant(-8),
    [Stub.abort]: () => Fail("abort"),
    [Stub.cxa_throw]: (ptr: number) => {
      throw ptr
    },
    [Stub.throw_longjmp]: () => {
      throw Infinity
    },
    [Stub.exit]: (status: number) => {
      throw { status }
    },
    [Stub.date_now]: DATE,
    [Stub.environ_sizes_get]: (countPtr: number, sizePtr: number) => {
      const heap = new DataView(mem().buffer)
      heap.setInt32(countPtr, 0, true)
      heap.setInt32(sizePtr, 0, true)
      return 0
    },
    [Stub.resize_heap]: (requestedSize: number) => {
      const m = mem()
      const oldSize = m.buffer.byteLength
      requestedSize >>>= 0
      if (requestedSize > MAX_HEAP) return 0
      for (let cutDown = 1; cutDown <= 4; cutDown *= 2) {
        let overGrownHeapSize = oldSize * (1 + 0.2 / cutDown)
        overGrownHeapSize = Min(overGrownHeapSize, requestedSize + 100663296)
        const newSize = Min(
          MAX_HEAP,
          Align(Max(requestedSize, overGrownHeapSize), 65536)
        )
        const pagesToGrow = ((newSize - oldSize + 65535) / 65536) | 0
        try {
          m.grow(pagesToGrow)
          return 1
        } catch {
          // try again with less overallocation
        }
      }
      return 0
    },
  }

  for (const [name, kind] of Items(config.stubs)) {
    moduleImports[name] = stubs[kind]
  }

  if (config.buildCustomImports) {
    const custom = config.buildCustomImports(exports)
    for (const [name, fn] of Items(custom)) {
      moduleImports[name] = fn
    }
  }

  const imports: WebAssembly.Imports = {
    [config.importModule]: moduleImports,
  }

  return { exports, imports }
}

// ── CreateWasmLoader ────────────────────────────────────────────────

/**
 * Compile a WASM module from a URL and return a factory that creates WasmInstance objects.
 * The module is compiled once; each call to the factory creates a fresh instance.
 */
export const CreateWasmLoader = async (
  url: string,
  config: WasmExportConfig
): Promise<() => Promise<WasmInstance>> => {
  const response = await UrlLoad(url, "application/wasm")
  const module = await WebAssembly.compileStreaming(response)

  return async () => {
    const { imports, exports } = CreateEmscriptenImports(config)

    const init = () =>
      WebAssembly.instantiate(module, imports).then(instance => {
        RecordAssign(exports, instance.exports)
        const ctors = exports[config.wasmCallCtors] as () => void
        ctors()
        if (config.onInit) config.onInit(exports)
      })

    await init()

    const getHeap = () =>
      new Uint8Array((exports[config.memory] as WebAssembly.Memory).buffer)

    const malloc = (size: Bytes): Allocated => {
      if (!config.malloc || !config.free) {
        return Fail(
          "malloc/free not available — module uses stack allocation only"
        )
      }
      const fn = exports[config.malloc] as (size: number) => number
      const pointer = fn(size) as Pointer
      const freeFn = exports[config.free] as (ptr: number) => void
      return { free: () => freeFn(pointer), pointer }
    }

    const writeTo = (ptr: Pointer, data: Uint8Array) => getHeap().set(data, ptr)

    const writeBytes = (data: Uint8Array): Allocated => {
      const { pointer, free } = malloc(data.length as Bytes)
      writeTo(pointer, data)
      return { free, pointer }
    }

    const writeString = (str: string): Allocated => {
      const bytes = StringToUInt8(str)
      const { pointer, free } = malloc((bytes.length + 1) as Bytes)
      writeTo(pointer, bytes)
      getHeap()[pointer + bytes.length] = 0
      return { free, pointer }
    }

    const readBytes = (ptr: Pointer, size: Bytes) =>
      new Uint8Array(
        (exports[config.memory] as WebAssembly.Memory).buffer,
        ptr,
        size
      ).slice()

    const readFloats = (ptr: Pointer, count: number) =>
      new Float32Array(
        (exports[config.memory] as WebAssembly.Memory).buffer,
        ptr,
        count
      )

    const setInt32 = (ptr: Pointer, value: number) =>
      new DataView(
        (exports[config.memory] as WebAssembly.Memory).buffer
      ).setInt32(ptr, value, true)

    const addCallback = (
      fn: (...args: number[]) => number,
      sig: string
    ): Allocated => {
      const wrapped = ConvertJsFunctionToWasm(fn, sig)
      const table = exports[config.indirectFunctionTable] as WebAssembly.Table
      const index = table.length
      table.grow(1)
      table.set(index, wrapped)
      const pointer = index as Pointer
      return {
        free: () =>
          (exports[config.indirectFunctionTable] as WebAssembly.Table).set(
            index,
            null
          ),
        pointer,
      }
    }

    const getMemorySize = () =>
      (exports[config.memory] as WebAssembly.Memory).buffer.byteLength as Bytes

    const dispose = async () => {
      for (const key of Keys(exports)) {
        delete exports[key]
      }
    }

    return {
      addCallback,
      dispose,
      exports,
      getMemorySize,
      init,
      malloc,
      readBytes,
      readFloats,
      setInt32,
      writeBytes,
      writeString,
      writeTo,
    }
  }
}
